import { useEffect, useMemo, useState } from 'react';
import { HttpError, useGo, useInvalidate, useParsed, useTranslation } from '@refinedev/core';
import { useAtom } from 'jotai';
import { eventIdAtom } from '../../core/calendar/state';
import { CalendarEvents } from '@components/organisms/CalendarEvents';
import { FaCalendar, FaPlus, FaRotateRight } from 'react-icons/fa6';
import { CalendarEditCreate } from '@components/organisms/CalendarEditCreate';
import { useModalForm } from '@refinedev/react-hook-form';
import { ModalSideWrapper } from '@components/molecules/ModalSideWrapper';
import { Calendar } from '@components/atoms/Calendar';
import dayjs from 'dayjs';
import { roundTime } from '@utils/time';
import { useAppCalendar } from 'hooks/calendar/useAppCalendar';
import { calendar_v3 } from "googleapis"
import { GoogleServicesTemplate } from '@components/templates/googleServices';
import { CanAccess } from '@refinedev/core';
import { EventCode, getMetadataString } from 'core/calendar/definitions';
import { logger } from '@utils/logger';

/**
 * Props for the CalendarList component.
 */
export interface CalendarListProps {

}

/**
 * Main calendar page component. Handles event creation, editing, and calendar navigation.
 * Responsive and uses Refine, TailwindCSS, and custom hooks.
 */
export function CalendarList(props: CalendarListProps) {
    const { translate } = useTranslation();
    const invalidate = useInvalidate()
    const [eventId, setEventId] = useAtom(eventIdAtom)
    const [date, setDate] = useState<string>()
    const parsed = useParsed()
    const go = useGo()

    // logger.debug("parsed params", parsed)

    // logger.debug(isLoading, auth)

    const calendarId = useAppCalendar()
    // logger.debug(calendarId)

    /**
     * Modal form for editing an event.
     */
    const formEdit = useModalForm<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        refineCoreProps: {
            dataProviderName: 'gapi',
            resource: `calendar/calendars/:calendar/events`,
            action: "edit",
            id: eventId,
            // syncWithLocation: true,
            // syncWithLocation: { key: "event", syncId: true },
            // id: '',
            meta: {
                params: {
                    calendar: calendarId?.availability
                }
            },
            queryOptions: {
                //     enabled: !!calendarId
                enabled: !!calendarId?.availability,
            }
        },
    })

    /**
     * Modal form for creating an availability event.
     */
    const formCreateAvailability = useModalForm<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        defaultValues: {
            summary: translate(`calendar-defs.events.${EventCode.AVAILABILITY}`),
            description: getMetadataString({
                code: EventCode.AVAILABILITY
            }),
            conferenceData: {
                createRequest: {
                    requestId: crypto.randomUUID(),
                    conferenceSolutionKey: {
                        type: 'hangoutsMeet'
                    }
                },
            },
            start: {
                dateTime: roundTime(dayjs()).toISOString(),
                timeZone: 'America/Bogota' // Set to a default English-speaking timezone
                // date: dayjs().startOf('day').toISOString()
            },
            end: {
                dateTime: roundTime(dayjs()).add(1, 'hour').toISOString(),
                timeZone: 'America/Bogota'
                // date: dayjs().startOf('day').add(1, 'day').toISOString()
            }
        },
        refineCoreProps: {
            dataProviderName: 'gapi',
            resource: `calendar/calendars/:calendar/events`,
            action: "create",
            meta: {
                params: {
                    calendar: calendarId?.availability
                },
                queryParams: {
                    conferenceDataVersion: 1,
                    enabled: !!calendarId?.availability,
                }
            },
        }
    })

    /**
     * Close the edit modal and reset form state.
     */
    const onCloseEditModal = () => {
        formEdit.modal.close()
        formEdit.reset()
        setEventId("")
    }

    /**
     * Close the create modal and reset form state.
     */
    const onCloseCreateModal = () => {
        formCreateAvailability.modal.close()
        formCreateAvailability.reset()
        setEventId("")
    }

    /**
     * Open the create event modal.
     */
    const createEvent = () => {
        formCreateAvailability.modal.show()
        setEventId("")
    }

    // If the URL has a ?create-event param, open the create modal automatically
    const createEventParam = parsed.params?.['create-event'] as boolean
    // logger.debug("create-event", createEventParam)
    useEffect(() => {
        if (createEventParam) {
            // logger.debug("open modal create event")
            createEvent()
        }
    }, [createEventParam])

    useEffect(() => {
        if (eventId)
            formEdit.modal.show(eventId)
    }, [eventId])

    // Memoized current date for calendar navigation
    const currentDate = useMemo(() => dayjs().startOf('day'), [])
    const minTime = dayjs(date).startOf('month')
    const maxTime = dayjs(date).startOf('month').add(1, 'month')
    // logger.debug(minTime, maxTime)

    /**
     * Refresh the calendar event list from the server.
     */
    const refreshCalendar = async () => {
        await invalidate({
            dataProviderName: 'gapi',
            resource: `calendar/calendars/:calendar/events`,
            invalidates: ['list']
        })
    }

    return <GoogleServicesTemplate
        subnavbar={<>
            <button
                className='hidden lg:inline-flex btn btn-sm btn-secondary btn-outline tooltip'
                onClick={refreshCalendar}
                data-tip={translate("common.reload")}
            >
                <FaRotateRight />
            </button>
            <CanAccess resource="appointment" action="create">
                <button
                    className='hidden lg:inline-flex btn btn-sm btn-secondary btn-outline'
                    onClick={() => go({ to: { resource: 'appointment', action: 'create' } })}
                    data-tip={translate("page.home.topButtons.schedule.tip")}
                >
                    <FaPlus /> {translate("page.home.topButtons.schedule.title")}
                </button>
            </CanAccess>
            <CanAccess resource="availability" action="create">
                <button
                    className='hidden lg:inline-flex btn btn-sm btn-secondary btn-outline tooltip'
                    onClick={createEvent}
                    data-tip={translate("page.home.topButtons.schedule.tip")}
                >
                    <FaPlus /> {translate("page.calendar.create-availability")}
                </button>
            </CanAccess>
        </>}
        actions={<>
            <CanAccess resource="appointment" action="create">
                <button
                    className="lg:hidden btn w-18 h-18 btn-primary btn-circle shadow-lg"
                    onClick={() => go({ to: { resource: 'appointment', action: 'create' } })}
                >
                    <FaPlus className='w-6 h-6' />
                </button>
            </CanAccess>
            <CanAccess resource="availability" action="create">
                <button
                    className="lg:hidden btn w-18 h-18 btn-primary btn-circle shadow-lg"
                    onClick={createEvent}
                >
                    <FaPlus className='w-6 h-6' />
                </button>
            </CanAccess>
        </>}
        sideaction={<>
            <ModalSideWrapper isOpen={formCreateAvailability.modal.visible || formEdit.modal.visible} onClose={() => { onCloseCreateModal(); onCloseEditModal() }} >
                <CalendarEditCreate className={formEdit.modal.visible ? '' : 'hidden'} form={formEdit} onClose={onCloseEditModal} />
                <CalendarEditCreate className={formCreateAvailability.modal.visible ? '' : 'hidden'} form={formCreateAvailability} onClose={onCloseCreateModal} />
            </ModalSideWrapper>
        </>
        }
        right={<div className='join'>
            <button
                className='lg:hidden btn btn-secondary join-item tooltip'
                onClick={refreshCalendar}
                data-tip={translate("common.reload")}
            >
                <FaRotateRight />
            </button>
            <div className="lg:hidden dropdown dropdown-bottom dropdown-end">
                <div tabIndex={0} role="button" className="btn btn-primary join-item"><FaCalendar /></div>
                <div tabIndex={0} className="dropdown-content card card-sm shadow-lg z-1 bg-base-100 w-72">
                    <Calendar onPageChange={(e) => setDate(e)} selectedDate={currentDate} className="card-body" />
                    <button
                        className="btn btn-primary mt-2 w-full"
                        onClick={() => setDate(dayjs().format('YYYY-MM-DD'))}
                    >{translate("common.today")}</button>
                </div>
            </div>
        </div>
        }
    >
        <div className='px-2 flex flex-1 flex-col items-center justify-start relative'>
            <div className='flex justify-start items-start w-full gap-6'>
                <div className='flex flex-col grow'>
                    <div className="rounded-2xl p-4 mb-4 shadow-lg border border-base-300">
                        <CalendarEvents minTime={minTime} maxTime={maxTime} />
                    </div>
                </div>
                <div className='px-8 hidden lg:block'>
                    <div className="sticky top-0 p-2 max-w-lg">
                        <Calendar onPageChange={(e) => setDate(e)} selectedDate={currentDate} className="card-body" />
                        <button
                            className="btn btn-xs btn-primary mt-2 w-full"
                            onClick={() => setDate(dayjs().format('YYYY-MM-DD'))}
                        >{translate("common.today")}</button>
                    </div>
                </div>
            </div>
        </div>
    </GoogleServicesTemplate>
}
